import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Table, PageHeader, Card, FormGroup, Button } from "@cofano/cck";

import { Box, Grid2 as Grid, Stack } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";

import { Language, usePermission } from "imports/Utils";
import ExternalMasterdataAlert, { ExternalMasterdataWarningIcon, MasterdataEntity } from "components/masterdata/ExternalMasterdataAlert";

import { GetFleetDocument, GetFleetQuery } from "__generated__/view/fleets/graphql/fleet.queries.generated";
import {
	AddShipToFleetDocument,
	AddShipToFleetMutation,
	RemoveShipFromFleetDocument,
	RemoveShipFromFleetMutation,
} from "__generated__/view/fleets/graphql/fleet.mutations.generated";

const FleetDetails = () => {

	const navigate = useNavigate();
	const { id } = useParams();
	const { isReadOnly } = usePermission();

	const { data, loading: fleetLoading, refetch } = useQuery<GetFleetQuery>(GetFleetDocument, {
		variables: {
			id: Number(id),
		},
	});

	const confirm = useConfirm();
	const [ selectedShip, selectShip ] = useState<ShipSelectOption>();

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: "fleetShips",
		content: data?.fleet?.ships || [],
		saveForOrganizationEnabled: false,
	});

	const [ addShipToFleet, { loading: addShipLoading } ] = useMutation<AddShipToFleetMutation>(AddShipToFleetDocument);
	const [ removeShipFromFleet, { loading: removeShipLoading } ] = useMutation<RemoveShipFromFleetMutation>(RemoveShipFromFleetDocument);

	const loading = fleetLoading || addShipLoading || removeShipLoading;

	const fleet = data?.fleet;

	const onRemoveShipFromFleet = (shipId: number) => {
		removeShipFromFleet({
			variables: {
				fleetId: Number(id), shipId,
			},
		}).then(() => {
			refetchAfterDelete();
			refetch();
		});
	};

	const onAddShip = (shipId: number) => {
		selectShip(undefined);
		addShipToFleet({
			variables: {
				fleetId: Number(id), shipId,
			},
		}).then(() => {
			refetch();
		});
	};

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("FLEETS_SINGLE")}
					<Helmet title={intl.formatMessage({ id: "FLEETS_SINGLE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/fleets")} variant="outlined">
						{Language("FLEETS")}
					</Button>

					{!isReadOnly && (
						<Button onClick={() => navigate(`/view/fleets/${id}/edit`)}>
							{Language("EDIT")}
						</Button>
					)}

					<ExternalMasterdataWarningIcon entity={MasterdataEntity.Fleet} />
				</PageHeader.Actions>
			</PageHeader>

			<ExternalMasterdataAlert entity={MasterdataEntity.Fleet} externalId={fleet?.externalId} detailMode />

			<Grid spacing={3} container>
				<Grid size={{ xs: 12, lg: 6 }}>
					<Stack spacing={3}>
						<Card>
							<FormGroup label={Language("DESCRIPTION")} labelPlacement="left-center" overflow="hidden">
								{fleet?.description || "-"}
							</FormGroup>
						</Card>

						<Card title={intl.formatMessage({ id: "SHIPS" })}>
							{!isReadOnly && (
								<Box sx={{ width: 300 }}>
									<ShipSelect
										value={selectedShip}
										onChange={(option) => selectShip(option || undefined)}
										getOptionDisabled={(option) => {
											const ships = (fleet && Array.isArray(fleet.ships)) ? fleet.ships : [];
											return ships.some(s => s.id === option.id);
										}}
									/>

									<div style={{ margin: "16px 0" }}>
										<Button
											disabled={!selectedShip || loading}
											onClick={() => {
												if (selectedShip) {
													onAddShip(selectedShip.id);
												}
											}}
											variant="outlined"
											size="small">
											{Language("ADD_BUTTON")}
										</Button>
									</div>
								</Box>
							)}

							<Table
								{...tableProps}
								contentLoading={loading}
								columns={[ {
									id: "name",
									title: intl.formatMessage({ id: "SHIPS_NAME" }),
								}, {
									id: "eni",
									title: intl.formatMessage({ id: "SHIPS_ENI" }),
								}, {
									id: "mmsi",
									title: intl.formatMessage({ id: "SHIPS_MMSI" }),
								} ]}
								rowActions={[ {
									icon: <DeleteIcon />,
									hidden: isReadOnly,
									onClick: (row) => {
										confirm({
											type: "DELETE",
										}).then(() => {
											onRemoveShipFromFleet(row.id);
										});
									},
								} ]} />
						</Card>
					</Stack>
				</Grid>
			</Grid>
		</React.Fragment>
	);
};

export default FleetDetails;
